// ========================================
// components/SessionTimeout.js - Session Timeout Manager
// ========================================
// PURPOSE: Automatically log out user after period of inactivity
// - Tracks user activity (click, keypress, scroll, mousemove)
// - Shows warning modal 60 seconds before logout
// - Allows user to stay logged in by clicking "Stay Logged In"
// ========================================

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const SessionTimeout = ({ timeoutMinutes = 20, warningMinutes = 1 }) => {
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(warningMinutes * 60);
  const timerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);

  // Convert minutes to milliseconds
  const timeoutMs = timeoutMinutes * 60 * 1000;
  const warningMs = warningMinutes * 60 * 1000;

  // ========================================
  // RESET ALL TIMERS
  // ========================================
const resetTimers = () => {
  // Clear existing timers
  if (timerRef.current) clearTimeout(timerRef.current);
  if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
  if (countdownIntervalRef.current) {
    clearInterval(countdownIntervalRef.current);
    countdownIntervalRef.current = null;
  }
  
  // Hide warning modal
  setShowWarning(false);
  setCountdown(warningMinutes * 60);
  
  // Set new timers
  warningTimerRef.current = setTimeout(() => {
    setShowWarning(true);
    startCountdown();
  }, timeoutMs - warningMs);
  
  timerRef.current = setTimeout(() => {
    logout();
  }, timeoutMs);
};

  // ========================================
  // START COUNTDOWN FOR WARNING MODAL
  // ========================================
const startCountdown = () => {
  if (countdownIntervalRef.current) {
    clearInterval(countdownIntervalRef.current);
    countdownIntervalRef.current = null;
  }
  
  countdownIntervalRef.current = setInterval(() => {
    setCountdown(prev => {
      if (prev <= 1) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
        // Auto-logout when countdown reaches 0
        logout();
        return 0;
      }
      return prev - 1;
    });
  }, 1000);
};

  // ========================================
  // EXTEND SESSION (user clicked "Stay Logged In")
  // ========================================
  const extendSession = () => {
    resetTimers();
  };

  // ========================================
  // LOGOUT USER
  // ========================================
  const logout = () => {
    // Clear all timers
    if (timerRef.current) clearTimeout(timerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    
    // Clear localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    
    // Redirect to login
    navigate('/login');
  };

// ========================================
// TRACK USER ACTIVITY
// ========================================
useEffect(() => {
  // List of events that count as user activity
  const activityEvents = [
    'mousedown', 'mousemove', 'keydown', 'scroll', 
    'click', 'touchstart', 'touchmove', 'wheel'
  ];
  
  const handleActivity = () => {
    // Only reset timers if warning modal is NOT showing
    // This prevents mouse movement from resetting during countdown
    if (!showWarning) {
      resetTimers();
    }
  };
  
  // Add event listeners
  activityEvents.forEach(event => {
    window.addEventListener(event, handleActivity);
  });
  
  // Start timers
  resetTimers();
  
  // Cleanup on unmount
  return () => {
    activityEvents.forEach(event => {
      window.removeEventListener(event, handleActivity);
    });
    if (timerRef.current) clearTimeout(timerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
  };
}, [showWarning]); // Add showWarning to dependency array

  // ========================================
  // RENDER WARNING MODAL
  // ========================================
  if (!showWarning) return null;

  return (
    <div className="session-modal-overlay">
      <div className="session-modal-content">
        <div className="session-modal-icon">⏰</div>
        <h2 className="session-modal-title">Session Expiring Soon</h2>
        <p className="session-modal-text">
          Your session will expire in <strong>{countdown}</strong> second{countdown !== 1 ? 's' : ''} due to inactivity.
        </p>
        <p className="session-modal-subtext">
          Click "Stay Logged In" to continue using Agyapadie Vault.
        </p>
        <div className="session-modal-buttons">
          <button onClick={extendSession} className="session-stay-btn">
            Stay Logged In
          </button>
          <button onClick={logout} className="session-logout-btn">
            Logout Now
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionTimeout;